/**
 * HTTP-backed rooms API for lifecycle operations.
 *
 * Handles room creation, joining, leaving, starting, and kicking via Cloud Functions.
 * Room data, messages, and subscriptions are handled by FirestoreRoomsApi.
 *
 * ⚠️ PLAYGROUND-ONLY: This file depends on Firebase (via callRemoteFunction) and should
 * ONLY be imported by PlaygroundHost. Do NOT import from barrel exports (rooms/index.ts).
 * This ensures Firebase is not bundled in production builds.
 */
import {
  CreateRoomOptions,
  JoinOrCreateRoomOptions,
  JoinOrCreateResult,
  ListRoomsOptions,
  StartRoomGameOptions,
} from './RoomsApi'
import {
  RundotGameRoom,
  RundotGameRoomPayload,
  RoomEnvelopeResponse,
  RoomsEnvelopeResponse,
  JoinOrCreateRoomEnvelopeResponse,
} from './RundotGameRoom'
import { callRemoteFunction, type RemoteFunctionInvoker } from '../http/callRemoteFunction'

/**
 * HTTP-based rooms API for lifecycle operations.
 */
export class HttpRoomsApi {
  private readonly appId: string
  private readonly callRemote: RemoteFunctionInvoker
  private readonly getProfileId: () => string

  constructor(
    appId: string,
    options: {
      callRemote?: RemoteFunctionInvoker
      getProfileId: () => string
    },
  ) {
    this.appId = appId
    this.callRemote = options.callRemote ?? callRemoteFunction
    this.getProfileId = options.getProfileId
  }

  async createRoomAsync(options: CreateRoomOptions): Promise<RundotGameRoom> {
    const response = await this.callRemote<RoomEnvelopeResponse>('apiCreateH5Room', {
      appId: this.appId,
      options: {
        maxPlayers: options.maxPlayers ?? 4,
        gameType: options.gameType,
        isPrivate: options.isPrivate ?? false,
        roomCode: options.roomCode,
        name: options.name,
        description: options.description,
        customMetadata: options.customMetadata,
        data: options.data,
      },
    })

    return new RundotGameRoom(response.room)
  }

  async joinOrCreateRoomAsync(options: JoinOrCreateRoomOptions): Promise<JoinOrCreateResult> {
    const response = await this.callRemote<JoinOrCreateRoomEnvelopeResponse>('apiJoinOrCreateH5Room', {
      appId: this.appId,
      options: {
        matchCriteria: options.matchCriteria,
        createOptions: {
          maxPlayers: options.createOptions.maxPlayers ?? 4,
          gameType: options.createOptions.gameType,
          isPrivate: options.createOptions.isPrivate ?? false,
          name: options.createOptions.name,
          description: options.createOptions.description,
          customMetadata: options.createOptions.customMetadata,
          data: options.createOptions.data,
        },
      },
    })

    return {
      action: response.action,
      room: new RundotGameRoom(response.room),
      playersJoined: response.playersJoined,
    }
  }

  async joinRoomByCodeAsync(roomCode: string): Promise<RundotGameRoom> {
    const response = await this.callRemote<RoomEnvelopeResponse>('apiJoinH5RoomByCode', {
      appId: this.appId,
      roomCode,
    })

    return new RundotGameRoom(response.room)
  }

  async getUserRoomsAsync(options?: ListRoomsOptions): Promise<RundotGameRoom[]> {
    const response = await this.callRemote<RoomsEnvelopeResponse>('apiGetUserH5Rooms', {
      appId: this.appId,
      includeArchived: options?.includeArchived ?? false,
    })

    return response.rooms.map((roomPayload) => new RundotGameRoom(roomPayload))
  }

  async leaveRoomAsync(room: RundotGameRoom): Promise<void> {
    await this.callRemote<void>('apiLeaveH5Room', {
      appId: this.appId,
      roomId: room.id,
    })
  }

  async kickPlayerAsync(
    room: RundotGameRoom,
    targetProfileId: string,
    options?: { reason?: string },
  ): Promise<void> {
    await this.callRemote<void>('apiKickH5RoomPlayer', {
      appId: this.appId,
      roomId: room.id,
      targetProfileId,
      reason: options?.reason,
    })
  }

  async startRoomGameAsync(room: RundotGameRoom, options?: StartRoomGameOptions): Promise<void> {
    // Backend contract: req.body must include { roomId, options: { gameConfig, turnOrder } }
    await this.callRemote<RoomEnvelopeResponse>('apiStartH5Game', {
      roomId: room.id,
      options: {
        gameConfig: options?.gameConfig ?? {},  // Default to {} like Production
        turnOrder: options?.turnOrder ?? null,
      },
    })
  }
}
